import { AnalyticsBrowser } from '@segment/analytics-next';

// ─── Initialize ────────────────────────────────────────────────────────────
const writeKey = process.env.REACT_APP_SEGMENT_WRITE_KEY;

let analytics = null;

if (writeKey) {
    analytics = AnalyticsBrowser.load({ writeKey });
} else {
    console.warn('[Segment] REACT_APP_SEGMENT_WRITE_KEY not set — tracking disabled.');
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const isBrowser = () => typeof window !== 'undefined';

/**
 * Read attribution/UTM params stored in localStorage by the existing gtm.js util.
 */
const getStoredUtm = () => {
    if (!isBrowser()) return {};
    return {
        utm_source: localStorage.getItem('utm_source') || null,
        utm_medium: localStorage.getItem('utm_medium') || null,
        utm_campaign: localStorage.getItem('utm_campaign') || null,
        utm_content: localStorage.getItem('utm_content') || null,
    };
};

/**
 * Read UTM params directly from the current URL.
 */
const getUrlUtm = () => {
    if (!isBrowser()) return {};
    const params = new URLSearchParams(window.location.search);
    return {
        utm_source: params.get('utm_source') || null,
        utm_medium: params.get('utm_medium') || null,
        utm_campaign: params.get('utm_campaign') || null,
        utm_content: params.get('utm_content') || null,
    };
};

const safeTrack = async (event, properties = {}) => {
    if (!analytics) return;
    try {
        await analytics.track(event, {
            timestamp: new Date().toISOString(),
            ...properties,
        });
    } catch (err) {
        console.error(`[Segment] track(${event}) failed:`, err.message);
    }
};

const safePage = async (pageName, properties = {}) => {
    if (!analytics) return;
    try {
        await analytics.page(pageName, {
            timestamp: new Date().toISOString(),
            ...properties,
        });
    } catch (err) {
        console.error(`[Segment] page(${pageName}) failed:`, err.message);
    }
};

const safeIdentify = async (userId, traits = {}) => {
    if (!analytics) return;
    try {
        await analytics.identify(userId, traits);
    } catch (err) {
        console.error(`[Segment] identify(${userId}) failed:`, err.message);
    }
};

// ─── 3.1 Signup Events (Frontend) ──────────────────────────────────────────

/**
 * page_viewed — call on every route change.
 * pageName: human-readable name e.g. 'landing', 'signup', 'kyc', 'investment'
 */
export const trackPageViewed = (pageName) => {
    const utm = { ...getStoredUtm(), ...getUrlUtm() };
    safePage(pageName, {
        page_name: pageName,
        utm_source: utm.utm_source,
        utm_medium: utm.utm_medium,
        utm_campaign: utm.utm_campaign,
        referrer_url: isBrowser() ? document.referrer || null : null,
    });
};

/**
 * signup_started — fire when user begins filling the registration form.
 * Call this on the first interaction with the signup form (e.g. onFocus of first field).
 */
export const trackSignupStarted = (referralCode = null) => {
    const utm = { ...getStoredUtm(), ...getUrlUtm() };
    safeTrack('signup_started', {
        utm_source: utm.utm_source,
        utm_medium: utm.utm_medium,
        utm_campaign: utm.utm_campaign,
        referral_code: referralCode || null,
    });
};

/**
 * identify — call after login to associate the browser session with a user.
 * userId: the backend user ID.
 * traits: any known user properties at login time.
 */
export const identifyUser = (userId, traits = {}) => {
    safeIdentify(String(userId), traits);
};

export default analytics;
